// News.js
import React, { useEffect, useMemo, useState } from 'react';
import api from "./context/api";
import { useDevice } from "./context/DeviceContext";
import { useAuth } from "./context/AuthContext";

const keywords = [
  "Acquisition", "Acquire", "Acquires", "Amalgamation", "Arrangement", "Merger",
  "Demerge", "De merge", "Demerger", "disinvestment",
  "Re-structuring", "Restructuring", "Offer For Sale",
  "Bonus", "split", "Buy back", "Buyback",
  "Rights issue", "Right issue",
  "Public offer", "public offering",
  "fund raise", "fund raising", "raise fund",
  "New Project", "new contract", "new order",
  "order received", "Awarding of order",
  "Bagging", "beg", "begs", "winning",
  "Expansion", "to invest", "settlement",
  "Raising of funds", "Rating", "clarification",
  "Partnership", "Joint venture", "Collaboration",
  "Capital expenditure", "capacity addition",
  "Strategic alliance", "Revenue growth",
  "Profit increase", "Earnings beat",
  "Record earnings", "Cost reduction",
  "Debt reduction", "Product launch",
  "New technology", "Innovation", "License",
  "Patent granted", "FDA approval",
  "Contract extension", "Market expansion",
  "Upgraded", "downgraded",
  "Buy recommendation", "Positive outlook",
  "Guidance raised", "Guidance",
  "Milestone achieved", "Supply agreement",
  "Exclusive agreement", "Patent filing",
  "Product approval", "Regulatory approval",
  "Secondary offering", "Share repurchase",
  "Equity infusion", "Capital raise",
  "Increase in volume", "Spurt in Volume",
  "Movement in price", "name change",
  "Demand Notice", "Incorporation",

  "margins", "margin", "policy", "deal",
  "jumps", "drops", "outperform",
  "rise", "fall", "jump", "Tariff", "down",
  "bags", "Results", "expands", "slumps",
  "Slip", "rose", "highest-ever",
  "record", "records", "high",
  "selling", "sell", "IPO", "listing",
  "downgrades", "upgrades", "reaffirmed",
  "surge", "muted", "climbs", "misses",
  "crashes", "crash", "grow", "raise",
  "Promoters", "Promoter", "plunge",
  "Dividend", "allots", "allot",
  "hike", "slump", "pledged", "pledge",
  "rises", "hikes", "growth", "raises",
  "FTA", "Upside", "downside",
  "Crashed", "valuation", "valuations",
  "gains", "Layoffs", "Layoff",
  "Rallies", "losses", "cautious",
  "outlook", "soar", "Plummet",
  "Bullish", "Bearish", "allegations",
  "gain", "commissions",

  "bypoll", "High Court", "Elections",
  "crisis", "crunch", "Supreme Court",
  "fraud", "Stake", "analysts","Price Target", 
  "NCLT", "SEBI","IRDAI","RBI","CBI",
  "DHRP", "delisting", "Upper Circuit",
  "Lower Circuit", "QIP",

  "Premji Invest","Zerodha", "kamath",
  "Tax Demand", "bail", "arrest", "GST", "FEMA", "violation",
  "dispute", "Prestigious", "Awards", "commissioned",

  "Target","Motilal Oswal",
  "shortage","Demand", "disrupted", "supply", "peak"
];

function News() {
  const { isMobile } = useDevice();
  const { user, initializing } = useAuth();

  const [stockNewsData, setStockNewsData] = useState([]);
  const [stockFeedNewsData, setStockFeedNewsData] = useState([]);
  const [otherFeedNewsData, setOtherFeedNewsData] = useState([]);
  const [indiaNewsData, setIndiaNewsData] = useState([]);
  const [globalNewsData, setGlobalNewsData] = useState([]); // API uses "global", UI shows "World"
  const [sitemapNewsData, setSitemapNewsData] = useState([]); // API uses "global", UI shows "World"
  const [forum1NewsData, setForum1NewsData] = useState([]); // API uses "global", UI shows "World"
  const [forum2NewsData, setForum2NewsData] = useState([]); // API uses "global", UI shows "World"
  const [forum3NewsData, setForum3NewsData] = useState([]); // youtube transcripts
  

  const [loading, setLoading] = useState(true);
  const [d1Sort, setD1Sort] = useState(null); // 'asc' | 'desc' | null
  

  // Keep exactly one expanded row per tab: { Stocks: <id|null>, India: <id|null>, World: <id|null> }
  const [expandedIdByTab, setExpandedIdByTab] = useState({
    Stocks: null,
    India: null,
    World: null,
  });

  const [activeTab, setActiveTab] = useState('Stocks'); // 'Stocks' | 'India' | 'World'
  const [selectedDate, setSelectedDate] = useState(null); // 'YYYY-MM-DD' or null

const [importantOnly, setImportantOnly] = useState(false);
const [selectedKeyword, setSelectedKeyword] = useState(null);
const [showImportantKeywords, setShowImportantKeywords] = useState(false);

const escapedKeywords = useMemo(
  () =>
    keywords
      .map((k) => k.trim())
      .filter(Boolean)
      .sort((a, b) => b.length - a.length) // longer first
      .map((k) => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')),
  []
);

const keywordRegex = useMemo(() => {
  if (!escapedKeywords.length) return null;
  return new RegExp(`\\b(${escapedKeywords.join('|')})\\b`, 'gi');
}, [escapedKeywords]);

const rowContainsImportantKeyword = (row) => {
  if (!escapedKeywords.length) return false;
  const regex = new RegExp(`\\b(${escapedKeywords.join('|')})\\b`, 'i');
  const text = `${row?.title ?? ''} ${getSummaryText(row)}`;
  return regex.test(text);
};

const getMatchedKeywordsForRow = (row) => {
  const text = `${row?.title ?? ''} ${getSummaryText(row)}`.toLowerCase();

  return keywords.filter((kw) => {
    const escaped = kw.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`\\b${escaped}\\b`, 'i');
    return regex.test(text);
  });
};

const highlightKeywords = (text) => {
  const str = (text ?? '').toString();
  if (!str || !escapedKeywords.length) return str;

  const regex = new RegExp(`\\b(${escapedKeywords.join('|')})\\b`, 'gi');
  const parts = str.split(regex);

  return parts.map((part, idx) => {
    const testRegex = new RegExp(`\\b(${escapedKeywords.join('|')})\\b`, 'i');
    if (part && testRegex.test(part)) {
      return (
        <mark
          key={idx}
          className="bg-yellow-200 text-gray-900 px-0.5 rounded"
        >
          {part}
        </mark>
      );
    }
    return <React.Fragment key={idx}>{part}</React.Fragment>;
  });
};

  useEffect(() => {
    if (initializing) return;
  }, [initializing, user]);

  // Fetch: Stocks
  useEffect(() => {
    api
      .get(`${process.env.REACT_APP_API_URL}/api/stockNews`)
      .then((res) => setStockNewsData(res.data || []))
      .catch((err) => console.error('Failed to fetch Stock News', err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    api
      .get(`${process.env.REACT_APP_API_URL}/api/otherFeedNews`)
      .then((res) => setOtherFeedNewsData(res.data || []))
      .catch((err) => console.error('Failed to fetch Stock News', err))
      .finally(() => {});
  }, []);

  useEffect(() => {
    api
      .get(`${process.env.REACT_APP_API_URL}/api/stockFeedNews`)
      .then((res) => setStockFeedNewsData(res.data || []))
      .catch((err) => console.error('Failed to fetch Stock News', err))
      .finally(() => {});
  }, []);

  // Fetch: India
  useEffect(() => {
    api
      .get(`${process.env.REACT_APP_API_URL}/api/indiaNews`)
      .then((res) => setIndiaNewsData(res.data || []))
      .catch((err) => console.error('Failed to fetch India News', err));
  }, []);

  // Fetch: Global (World)
  useEffect(() => {
    api
      .get(`${process.env.REACT_APP_API_URL}/api/globalNews`)
      .then((res) => setGlobalNewsData(res.data || []))
      .catch((err) => console.error('Failed to fetch Global/World News', err));
  }, []);


  useEffect(() => {
    api
      .get(`${process.env.REACT_APP_API_URL}/api/sitemapNews`)
      .then((res) => setSitemapNewsData(res.data || []))
      .catch((err) => console.error('Failed to fetch sitemap News', err));
  }, []);

  useEffect(() => {
    api
      .get(`${process.env.REACT_APP_API_URL}/api/forum1News`)
      .then((res) => setForum1NewsData(res.data || []))
      .catch((err) => console.error('Failed to fetch forum1 News', err));
  }, []);

  useEffect(() => {
    api
      .get(`${process.env.REACT_APP_API_URL}/api/forum2News`)
      .then((res) => setForum2NewsData(res.data || []))
      .catch((err) => console.error('Failed to fetch forum2 News', err));
  }, []);

  useEffect(() => {
    api
      .get(`${process.env.REACT_APP_API_URL}/api/forum3News`)
      .then((res) => setForum3NewsData(res.data || []))
      .catch((err) => console.error('Failed to fetch forum3 News', err));
  }, []);

  // Helpers
  const toDate = (v) => {
    const raw = v?.published ?? v?.published_parsed ?? '';
    const d = new Date(raw);
    return isNaN(d) ? null : d;
  };
  const ymd = (d) => {
    if (!(d instanceof Date)) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
  };
  const formatDatePretty = (dateStr) => {
    const d = new Date(dateStr);
    if (isNaN(d)) return '—';
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };
  const getSummaryText = (row) => (row?.summary ?? row?.Summary ?? '').toString();

  // Shared sorter (desc)
  const sortByPublishedDesc = (arr) =>
    [...(arr || [])].sort((a, b) => {
      const A = toDate(a);
      const B = toDate(b);
      if (!A && !B) return 0;
      if (!A) return 1;
      if (!B) return -1;
      return B - A;
    });

  // Current tab data
  const currentDataRaw = useMemo(() => {
    switch (activeTab) {
      case 'India':
        return indiaNewsData;
      case 'World':
        return globalNewsData;
      case 'Stocks Feed':
        return stockFeedNewsData;
      case 'Other':
        return otherFeedNewsData;
      case 'Sitemap':
        return sitemapNewsData;
      case 'Forum1':
        return forum1NewsData;
      case 'Forum2':
        return forum2NewsData;
      case 'Forum3':
        return forum3NewsData;
      case 'Stocks':
      default:
        return stockNewsData;
    }
  }, [activeTab, stockNewsData, indiaNewsData, globalNewsData, stockFeedNewsData, otherFeedNewsData, sitemapNewsData, forum1NewsData, forum2NewsData, forum3NewsData]);


  const currentDataSorted = useMemo(() => {
    const base = sortByPublishedDesc(currentDataRaw);

    const canSortD1 = activeTab === 'Stocks Feed' || activeTab === 'Other';
    if (!canSortD1 || !d1Sort) return base;

    return [...base].sort((a, b) => {
      const av = Number.parseFloat(a?.change_1D);
      const bv = Number.parseFloat(b?.change_1D);

      const aBad = !Number.isFinite(av);
      const bBad = !Number.isFinite(bv);

      // If both are missing/non-numeric, fall back to published desc (keep feed stable)
      if (aBad && bBad) {
        const A = a?.published ? new Date(a.published).getTime() : 0;
        const B = b?.published ? new Date(b.published).getTime() : 0;
        return B - A;
      }

      // Push non-numeric to the end irrespective of direction
      if (aBad) return 1;
      if (bBad) return -1;

      // Proper numeric compare
      return d1Sort === 'asc' ? av - bv : bv - av;
    });
  }, [currentDataRaw, activeTab, d1Sort]);

  // Unique dates (for active tab)
  const uniqueDates = useMemo(() => {
    const set = new Set();
    currentDataSorted.forEach((row) => {
      const d = toDate(row);
      if (d) set.add(ymd(d));
    });
    return Array.from(set).sort((a, b) => (a < b ? 1 : -1)); // desc
  }, [currentDataSorted]);

  const keywordCounts = useMemo(() => {
    const map = new Map();

    currentDataSorted.forEach((row) => {
      if (selectedDate) {
        const d = toDate(row);
        if (!d || ymd(d) !== selectedDate) return;
      }

      const matched = getMatchedKeywordsForRow(row);

      matched.forEach((kw) => {
        map.set(kw, (map.get(kw) || 0) + 1);
      });
    });

    return Array.from(map.entries())
      .map(([keyword, count]) => ({ keyword, count }))
      .sort((a, b) => b.count - a.count || a.keyword.localeCompare(b.keyword));
  }, [currentDataSorted, selectedDate]);

  // Apply date filter
  const filteredData = useMemo(() => {
    let rows = currentDataSorted;

    if (selectedDate) {
      rows = rows.filter((row) => {
        const d = toDate(row);
        return d && ymd(d) === selectedDate;
      });
    }

    if (selectedKeyword) {
      const escaped = selectedKeyword.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`\\b${escaped}\\b`, 'i');

      rows = rows.filter((row) => {
        const text = `${row?.title ?? ''} ${getSummaryText(row)}`;
        return regex.test(text);
      });
    }

    return rows;
  }, [currentDataSorted, selectedDate, selectedKeyword]);

  // If the active tab changes, we keep its own expanded selection.
  // If filters change and the previously expanded item is no longer visible, collapse it.
  useEffect(() => {
    const currentExpandedId = expandedIdByTab[activeTab];
    if (!currentExpandedId) return;
    const stillVisible = filteredData.some((row) => getRowId(row) === currentExpandedId);
    if (!stillVisible) {
      setExpandedIdByTab((prev) => ({ ...prev, [activeTab]: null }));
    }
  }, [filteredData, activeTab, expandedIdByTab]);

  // Styles
  const dateChipClass = (isActive) =>
    `px-2 py-1 text-xs rounded-full border ${
      isActive
        ? 'bg-blue-600 text-white border-blue-600'
        : 'bg-white text-gray-700 hover:bg-gray-100 border-gray-300'
    }`;

  // Tabs list
  const tabs = ['Stocks', 'India', 'World', 'Stocks Feed', 'Other', 'Sitemap', 'Forum1', 'Forum2', 'Forum3'];

  // Toggle handler to ensure only one expanded per tab
  const toggleExpandOne = (row) => {
    const rowId = getRowId(row);
    setExpandedIdByTab((prev) => ({
      ...prev,
      [activeTab]: prev[activeTab] === rowId ? null : rowId,
    }));
  };

  const getRowId = (row) =>
    row?.title_hash ||
    row?.link ||
    `${row?.title}|${row?.published ?? row?.published_parsed ?? ''}`;

  // --- CSV helpers
  const csvEscape = (v) => {
    const s = (v ?? "").toString();
    // escape quotes and wrap in quotes if needed
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  const buildCsvForTab = (tab, rows) => {
    // Keep columns stable across downloads, but include extra fields for feed tabs.
    const isFeedTab = tab === "Stocks Feed" || tab === "Other";

    const headers = isFeedTab
      ? ["published_date", "title", "source", "feed_key", "symbol", "change_1D", "link", "summary"]
      : ["published_date", "title", "source", "link", "summary"];

    const lines = [headers.join(",")];

    for (const row of rows || []) {
      const publishedStr = row?.published ?? row?.published_parsed ?? "";
      const publishedDate = (() => {
        const d = toDate(row);
        return d ? ymd(d) : "";
      })();

      const base = {
        published_date: publishedDate || publishedStr,
        title: row?.title ?? "",
        source: row?.source ?? "",
        link: row?.link ?? "",
        summary: getSummaryText(row),
      };

      const extra = isFeedTab
        ? {
            feed_key: row?.feed_key ?? "",
            symbol: row?.symbol ?? "",
            change_1D:
              Number.isFinite(parseFloat(row?.change_1D))
                ? parseFloat(row.change_1D).toFixed(2)
                : "",
          }
        : {};

      const record = { ...base, ...extra };

      const line = headers.map((h) => csvEscape(record[h])).join(",");
      lines.push(line);
    }

    return lines.join("\n");
  };

  const downloadCsv = () => {
    const datePart = selectedDate ? selectedDate : "all-dates";
    const safeTab = (activeTab || "News").replace(/\s+/g, "_").toLowerCase();
    const filename = `news_${safeTab}_${datePart}.csv`;

    const csv = buildCsvForTab(activeTab, filteredData);

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();

    URL.revokeObjectURL(url);
  };


  return (
    <div className="p-4 mb-4">

      {/* Top Tabs (border-bottom link style) */}
      <div className="flex flex-wrap gap-2 border-b mb-2 pb-1">
        {tabs.map((tab) => (
          <a
            key={tab}
            href="#"
            onClick={(e) => {
              e.preventDefault();
              // Switching tabs preserves its own expansion slot
              // (no need to reset selectedDate unless you want to)
              setActiveTab(tab);
            }}
            className={`px-3 py-1 text-sm font-medium transition border-b-2 ${
              activeTab === tab
                ? 'border-blue-600 text-blue-600 font-semibold'
                : 'border-transparent text-gray-500 hover:text-blue-500 hover:border-blue-300'
            }`}
            style={{ minWidth: 'fit-content' }}
          >
            {tab}
          </a>
        ))}
      </div>

      {/* Date filter */}
      <div className="flex items-center gap-2 flex-wrap mb-2">
        <span className="text-xs text-gray-600 mr-1">Filter by date:</span>
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            setSelectedDate(null);
          }}
          className={dateChipClass(!selectedDate)}
          title="Show all dates"
        >
          All Dates
        </a>
        {uniqueDates.map((d) => {
          const label = formatDatePretty(d);
          return (
            <a
              key={d}
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setSelectedDate(d);
              }}
              className={dateChipClass(selectedDate === d)}
              title={label}
            >
              {label}
            </a>
          );
        })}
      </div>

      {/* Total rows after filters */}
      <div className="text-sm mt-3 text-gray-600 flex flex-wrap items-center gap-2">
        <div className="mb-1 flex items-center justify-between gap-3">
          <div className="text-gray-500 text-sm">
            Updates daily 11 PM, Total rows - {filteredData.length}
          </div>

          <button
            onClick={downloadCsv}
            className="flex-none px-3 py-1 text-xs border rounded bg-emerald-600 text-white hover:bg-emerald-700"
            title="Download CSV for current tab + current date filter"
          >
            Export CSV
          </button>
        </div>

        {/* 1D sort toggle — only for Stocks Feed / Other */}
        {(activeTab === 'Stocks Feed' || activeTab === 'Other') && (
          <span className="ml-1">
            <button
              onClick={() =>
                setD1Sort((s) => (s === null ? 'desc' : s === 'desc' ? 'asc' : null))
              }
              className="px-1 py-1 rounded-md border text-sm"
              title="Sort by 1D % change"
            >
              1D%  {d1Sort === 'asc' ? '↑' : d1Sort === 'desc' ? '↓' : '•'}
            </button>
          </span>
        )}
      </div>

      {!isMobile && (
        <div className="mt-2 mb-2">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setShowImportantKeywords((v) => !v)}
              className="px-3 py-1 text-xs rounded-full border bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
              title="Show or hide important keywords"
            >
              Important keywords {showImportantKeywords ? "▲" : "▼"}
            </button>

            {selectedKeyword && (
              <span className="text-xs text-gray-500">
                Showing news for: {selectedKeyword}
              </span>
            )}
          </div>

          {showImportantKeywords && (
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <button
                onClick={() => setSelectedKeyword(null)}
                className={`px-3 py-1 text-xs rounded-full border ${
                  selectedKeyword === null
                    ? "bg-amber-500 text-white border-amber-500"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                }`}
                title="Show all news"
              >
                All
              </button>

              {keywordCounts.map(({ keyword, count }) => (
                <button
                  key={keyword}
                  onClick={() =>
                    setSelectedKeyword((prev) => (prev === keyword ? null : keyword))
                  }
                  className={`px-3 py-1 text-xs rounded-full border ${
                    selectedKeyword === keyword
                      ? "bg-amber-500 text-white border-amber-500"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                  }`}
                  title={`Show news containing ${keyword}`}
                >
                  {keyword} ({count})
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="text-center text-blue-600 font-medium py-6 animate-pulse">Loading...</div>
      ) : (
        <>
          {/* Table */}
          <div className="overflow-x-hidden">
            <table className="table-fixed border-collapse w-full text-sm text-gray-800 font-normal">
              <thead>
                <tr className="bg-gray-200">
                  <th className="p-2 text-left">News & Stories</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.length === 0 ? (
                  <tr>
                    <td colSpan="3" className="text-center text-gray-500 py-4">
                      No News found.
                    </td>
                  </tr>
                ) : (
                  filteredData.map((row, idx) => {
                    const rowId = getRowId(row);
                    const isExpanded = expandedIdByTab[activeTab] === rowId;

                    const publishedStr = row?.published ?? row?.published_parsed ?? '';
                    const summary = getSummaryText(row);

                    return (
                      <tr key={rowId} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-100'}>

                        {/* Title + summary with +/- button */}
                        <td className="p-2">
                          <div className="flex items-start gap-2">
                            <button
                              onClick={() => toggleExpandOne(row)}
                              aria-expanded={isExpanded}
                              aria-controls={`summary-${idx}`}
                              className="inline-flex h-5 w-6 items-center justify-center rounded border border-gray-300 hover:bg-gray-100 text-gray-700"
                              title={isExpanded ? 'Collapse summary' : 'Expand summary'}
                            >
                              {isExpanded ? '−' : '+'}
                            </button>

                            <div className="flex-1">
                              <div className="font-medium">
                                <span>
                                  {highlightKeywords(row.title || '—')}
                                </span>

                                <span className="ml-2 text-xs leading-relaxed">
                                {row.link ? (
                                  <a
                                    href={row.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 underline"
                                  >
                                    {(row.source ? row.source : '—') + (row.channel ? ` - ${row.channel}` : row.feed_key ? ` - ${row.feed_key}` : '')}
                                  </a>
                                ) : (
                                  (row.source ? row.source : '—') + (row.channel ? ` - ${row.channel}` : row.feed_key ? ` - ${row.feed_key}` : '')
                                )}
                                </span>
                          
                                <span className="ml-2 text-xs text-gray-700 leading-relaxed">
                                {"(" + formatDatePretty(publishedStr) + ")"}
                                </span>

                                {/* 1D change: show only if numeric; color green/red */}
                                {Number.isFinite(parseFloat(row?.change_1D)) && (
                                  <span
                                    className={`ml-2 text-xs leading-relaxed ${
                                      parseFloat(row.change_1D) >= 0 ? "text-green-600" : "text-red-600"
                                    }`}
                                  >
                                    {`1D ${parseFloat(row.change_1D).toFixed(2)}%`}
                                  </span>
                                )}

                                {/* Symbol: show only if present; link to /symbol/{symbol} */}
                                {row?.symbol && (
                                  <a
                                    href={`symbol/${row.symbol}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="ml-2 text-xs text-blue-600 underline leading-relaxed"
                                  >
                                    {row.symbol}
                                  </a>
                                )}

                              </div>

                              {isExpanded && summary && (
                                <div
                                  id={`summary-${idx}`}
                                  className="mt-1 text-xs text-gray-600 leading-relaxed"
                                >
                                  {highlightKeywords(summary)}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export default News;
